// Crew-scoped double-booking detection for the schedule creator.
//
// A crew member is one person and cannot film two things at once, so any two
// assignments for the same crew member on the same day whose filming times
// overlap are a conflict. This module holds that decision as pure, testable
// logic so every placement path (drag, drop, resize, custom slot, and the
// artist detail dialog) enforces the same rule instead of re-implementing it.

import { toMinutes } from "@/lib/assignmentClipboard";

/**
 * Do two clock ranges overlap? Times may be "HH:MM" or "HH:MM:SS" — both are
 * compared in whole minutes (via the shared `toMinutes`), so an assignment
 * ending exactly at "12:00:00" does NOT overlap one starting at "12:00"
 * (touching edges are not a conflict).
 */
export function timeRangesOverlap(
  start1: string,
  end1: string,
  start2: string,
  end2: string,
): boolean {
  // Overlap iff each range starts strictly before the other one ends.
  return toMinutes(start1) < toMinutes(end2) && toMinutes(start2) < toMinutes(end1);
}

/** The minimum shape this module needs from a crew assignment. */
export interface ConflictAssignment {
  crew_member_id: string;
  event_date: string;
  filming_start: string;
  filming_end: string;
}

export interface CrewConflictQuery<T extends ConflictAssignment = ConflictAssignment> {
  crewMemberId: string;
  eventDate: string;
  start: string;
  end: string;
  /**
   * Optional predicate marking assignments to ignore — the one being moved or
   * edited (and, when re-assigning a crew member to an artist, the block that
   * placement replaces). Return true to skip that assignment. Receives the
   * caller's own assignment type so richer fields stay available.
   */
  ignore?: (assignment: T) => boolean;
}

/**
 * Would placing/editing an assignment at [start, end] double-book this crew
 * member? Only their own assignments on the same day are considered; the
 * `ignore` predicate excludes the block being replaced or edited so a no-op
 * save doesn't report a conflict with itself.
 */
export function crewMemberHasConflict<T extends ConflictAssignment>(
  assignments: readonly T[],
  { crewMemberId, eventDate, start, end, ignore }: CrewConflictQuery<T>,
): boolean {
  return assignments.some((a) => {
    if (a.crew_member_id !== crewMemberId) return false;
    if (a.event_date !== eventDate) return false;
    if (ignore?.(a)) return false;
    return timeRangesOverlap(start, end, a.filming_start, a.filming_end);
  });
}
